import { messages as sourceMessages } from 'locales/ru/messages.po';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import { getDateLocale } from './date-locale';
import { i18n, Messages } from './i18n';
import { AppLocale, SOURCE_LOCALE } from './locales';

export type CatalogLoader = (locale: AppLocale) => Promise<Messages>;

/**
 * Русский каталог — в основном бандле, а не чанком: в production макросы вырезают исходный текст,
 * так что без каталога даже русский интерфейс покажет пустоту. Он же — запасной вариант, когда
 * чанк другого языка не загрузился.
 */
const CATALOG_LOADERS: Record<AppLocale, () => Promise<Messages>> = {
  ru: async () => sourceMessages,
  en: async () => (await import('locales/en/messages.po')).messages,
};

const loadBundledCatalog: CatalogLoader = (locale) => CATALOG_LOADERS[locale]();

function applyDocumentLocale(locale: AppLocale) {
  document.documentElement.lang = locale;
  registerLocale(locale, getDateLocale(locale));
  setDefaultLocale(locale);
}

/**
 * Загружает каталог и переключает язык: `I18nProvider` перерисует дерево, `<html lang>` и
 * react-datepicker переключатся здесь же. Не бросает — если каталог не загрузился (сеть),
 * включается русский из бандла, иначе приложение, ждущее активации перед рендером, не стартует.
 *
 * @returns фактически включённый язык.
 */
export async function activateLocale(locale: AppLocale, loadCatalog: CatalogLoader = loadBundledCatalog): Promise<AppLocale> {
  let active = locale;
  let messages: Messages;

  try {
    messages = await loadCatalog(locale);
  } catch (error) {
    console.error(`[i18n] Каталог «${locale}» не загрузился, включён «${SOURCE_LOCALE}»`, error);
    active = SOURCE_LOCALE;
    messages = sourceMessages;
  }

  i18n.loadAndActivate({ locale: active, messages });
  applyDocumentLocale(active);
  return active;
}
